import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '@/App';
import {
  Sparkles,
  TrendingUp,
  Flame,
  DollarSign,
  Zap,
  Target,
  AlertTriangle,
  Shield,
  BarChart3,
  Link as LinkIcon,
  Play,
  Pause
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Intelligence = () => {
  const { user } = useContext(AuthContext);
  const [creators, setCreators] = useState([]);
  const [selectedCreator, setSelectedCreator] = useState('');
  
  const [streakStats, setStreakStats] = useState(null);
  const [monetizationStats, setMonetizationStats] = useState(null);
  const [autoPilotSettings, setAutoPilotSettings] = useState(null);
  const [syncEvents, setSyncEvents] = useState([]);
  
  const [showAutoPilotModal, setShowAutoPilotModal] = useState(false);
  const [showStreakSaverModal, setShowStreakSaverModal] = useState(false);

  useEffect(() => {
    fetchCreators();
  }, []);

  useEffect(() => {
    if (selectedCreator) {
      fetchStreakStats();
      fetchMonetizationStats();
      fetchAutoPilotSettings();
      // fetchSyncEvents() - REMOVED (EasyMoney deprecated)
    }
  }, [selectedCreator]);

  const fetchCreators = async () => {
    try {
      const orgsRes = await axios.get(`${API}/organizations`, { withCredentials: true });
      if (orgsRes.data.length > 0) {
        const creatorsRes = await axios.get(`${API}/creators?org_id=${orgsRes.data[0].id}`, { withCredentials: true });
        setCreators(creatorsRes.data);
        if (creatorsRes.data.length > 0) {
          setSelectedCreator(creatorsRes.data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch creators:', error);
    }
  };

  const fetchStreakStats = async () => {
    try {
      const response = await axios.get(`${API}/intelligence/streak-stats/${selectedCreator}`, { withCredentials: true });
      setStreakStats(response.data);
    } catch (error) {
      console.error('Failed to fetch streak stats:', error);
    }
  };

  const fetchMonetizationStats = async () => {
    try {
      const response = await axios.get(`${API}/intelligence/monetization/${selectedCreator}`, { withCredentials: true });
      setMonetizationStats(response.data);
    } catch (error) {
      console.error('Failed to fetch monetization stats:', error);
    }
  };

  const fetchAutoPilotSettings = async () => {
    try {
      const response = await axios.get(`${API}/intelligence/autopilot/${selectedCreator}`, { withCredentials: true });
      setAutoPilotSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch AutoPilot settings:', error);
    }
  };

  // fetchSyncEvents - REMOVED (EasyMoney deprecated)

  const handleUseStreakSaver = async () => {
    try {
      await axios.post(`${API}/intelligence/streak-saver/${selectedCreator}`, {}, { withCredentials: true });
      toast.success('Streak saved successfully!');
      setShowStreakSaverModal(false);
      fetchStreakStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to use Streak Saver');
    }
  };

  const handleToggleAutoPilot = async (enabled) => {
    try {
      await axios.put(`${API}/intelligence/autopilot/${selectedCreator}`, { enabled }, { withCredentials: true });
      toast.success(enabled ? 'AutoPilot enabled!' : 'AutoPilot disabled');
      fetchAutoPilotSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle AutoPilot');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  return (
    <div className="p-8 space-y-8" data-testid="intelligence-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">
            <span className="bg-gradient-to-r from-[#3A7BFF] to-[#39E6C8] bg-clip-text text-transparent">
              Intelligence Engine
            </span>
          </h1>
          <p className="text-[#8A93A6] text-lg">Track streaks, forecast revenue, and automate your workflow</p>
        </div>
      </div>

      {/* Creator Selector */}
      <div className="glass rounded-2xl p-6">
        <label className="block text-sm font-medium text-[#8A93A6] mb-2">Creator</label>
        <Select value={selectedCreator} onValueChange={setSelectedCreator}>
          <SelectTrigger className="max-w-md">
            <SelectValue placeholder="Select creator" />
          </SelectTrigger>
          <SelectContent>
            {creators.map(creator => (
              <SelectItem key={creator.id} value={creator.id}>{creator.display_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Streak Tracker */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#FF4F4F]/20 flex items-center justify-center">
              <Flame size={24} className="text-[#FF4F4F]" />
            </div>
            <h2 className="text-xl font-bold text-white">Consistency Streak</h2>
          </div>
          {streakStats && streakStats.streak_at_risk && (
            <Dialog open={showStreakSaverModal} onOpenChange={setShowStreakSaverModal}>
              <DialogTrigger asChild>
                <button className="btn-secondary flex items-center gap-2">
                  <Shield size={20} />
                  Use Streak Saver
                  <span className="px-2 py-1 rounded-full text-xs bg-[#39E6C8]/20 text-[#39E6C8]">Pro+</span>
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0E1A] border border-white/10">
                <DialogHeader>
                  <DialogTitle className="text-white">Streak Saver</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <p className="text-[#8A93A6]">
                    Your streak is at risk! Use Streak Saver to preserve your consistency score without posting today.
                  </p>
                  <div className="p-4 rounded-xl bg-[#FF4F4F]/10 border border-[#FF4F4F]/20">
                    <p className="text-sm text-white">
                      Current Streak: <span className="font-bold">{streakStats.current_streak} days</span>
                    </p>
                    <p className="text-sm text-[#8A93A6] mt-1">
                      Times used: {streakStats.streak_saver_used_count}
                    </p>
                  </div>
                  <button onClick={handleUseStreakSaver} className="btn-primary w-full">
                    Save My Streak
                  </button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {streakStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-xl bg-white/5">
              <p className="text-sm text-[#8A93A6] mb-1">Current Streak</p>
              <p className="text-3xl font-bold text-white">{streakStats.current_streak}</p>
              <p className="text-xs text-[#8A93A6]">days</p>
            </div>
            <div className="p-4 rounded-xl bg-white/5">
              <p className="text-sm text-[#8A93A6] mb-1">Longest Streak</p>
              <p className="text-3xl font-bold text-[#39E6C8]">{streakStats.longest_streak}</p>
              <p className="text-xs text-[#8A93A6]">days</p>
            </div>
            <div className="p-4 rounded-xl bg-white/5">
              <p className="text-sm text-[#8A93A6] mb-1">Consistency Score</p>
              <p className="text-3xl font-bold text-[#3A7BFF]">{streakStats.consistency_score}</p>
              <p className="text-xs text-[#8A93A6]">/100</p>
            </div>
            <div className="p-4 rounded-xl bg-white/5">
              <p className="text-sm text-[#8A93A6] mb-1">Weekly Average</p>
              <p className="text-3xl font-bold text-white">{streakStats.weekly_average}</p>
              <p className="text-xs text-[#8A93A6]">posts/week</p>
            </div>
          </div>
        )}
        
        {streakStats && streakStats.streak_at_risk && (
          <div className="mt-4 p-4 rounded-xl bg-[#FF4F4F]/10 border border-[#FF4F4F]/20 flex items-center gap-3">
            <AlertTriangle size={20} className="text-[#FF4F4F]" />
            <p className="text-white text-sm">
              Your streak is at risk! Post soon to maintain your {streakStats.current_streak}-day streak.
            </p>
          </div>
        )}
      </div>

      {/* Monetization Forecast */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-[#29E07F]/20 flex items-center justify-center">
            <DollarSign size={24} className="text-[#29E07F]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Monetization Forecast</h2>
            {monetizationStats && !monetizationStats.is_monetized && (
              <p className="text-xs text-[#8A93A6]">Estimates based on typical creator performance</p>
            )}
          </div>
        </div>

        {monetizationStats && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-white/5">
                <p className="text-sm text-[#8A93A6] mb-1">Monthly Est.</p>
                <p className="text-2xl font-bold text-[#29E07F]">
                  {formatCurrency(monetizationStats.estimated_monthly_revenue)}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-white/5">
                <p className="text-sm text-[#8A93A6] mb-1">6-Month Projection</p>
                <p className="text-2xl font-bold text-white">
                  {formatCurrency(monetizationStats.projected_6_month_revenue)}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-white/5">
                <p className="text-sm text-[#8A93A6] mb-1">12-Month Projection</p>
                <p className="text-2xl font-bold text-[#3A7BFF]">
                  {formatCurrency(monetizationStats.projected_12_month_revenue)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 rounded-xl bg-white/5">
                <p className="text-xs text-[#8A93A6]">Avg Views/Post</p>
                <p className="text-lg font-bold text-white">{monetizationStats.avg_views_per_post.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-xl bg-white/5">
                <p className="text-xs text-[#8A93A6]">Engagement Rate</p>
                <p className="text-lg font-bold text-white">{monetizationStats.avg_engagement_rate}%</p>
              </div>
              <div className="p-3 rounded-xl bg-white/5">
                <p className="text-xs text-[#8A93A6]">Est. CPM</p>
                <p className="text-lg font-bold text-white">${monetizationStats.estimated_cpm}</p>
              </div>
              <div className="p-3 rounded-xl bg-white/5">
                <p className="text-xs text-[#8A93A6]">Growth Rate</p>
                <p className="text-lg font-bold text-[#29E07F]">+{monetizationStats.revenue_growth_rate}%</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* AutoPilot */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3A7BFF] to-[#39E6C8] flex items-center justify-center">
              <Zap size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">AutoPilot</h2>
              <span className="px-2 py-1 rounded-full text-xs bg-[#39E6C8]/20 text-[#39E6C8]">Pro+ Feature</span>
            </div>
          </div>
          {autoPilotSettings && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-[#8A93A6]">
                {autoPilotSettings.enabled ? 'Enabled' : 'Disabled'}
              </span>
              <Switch
                checked={autoPilotSettings.enabled}
                onCheckedChange={handleToggleAutoPilot}
              />
            </div>
          )}
        </div>

        {autoPilotSettings && (
          <div className="space-y-4">
            <p className="text-[#8A93A6] text-sm">
              AutoPilot automates your entire content workflow: from upload to clip generation, optimization, and scheduling.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-xl bg-white/5 flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${autoPilotSettings.auto_clip_generation ? 'bg-[#29E07F]' : 'bg-[#8A93A6]'}`}></div>
                <span className="text-sm text-white">Auto Clips</span>
              </div>
              <div className="p-3 rounded-xl bg-white/5 flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${autoPilotSettings.auto_optimization ? 'bg-[#29E07F]' : 'bg-[#8A93A6]'}`}></div>
                <span className="text-sm text-white">Auto Optimize</span>
              </div>
              <div className="p-3 rounded-xl bg-white/5 flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${autoPilotSettings.auto_scheduling ? 'bg-[#29E07F]' : 'bg-[#8A93A6]'}`}></div>
                <span className="text-sm text-white">Auto Schedule</span>
              </div>
              <div className="p-3 rounded-xl bg-white/5 flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${autoPilotSettings.auto_branding ? 'bg-[#29E07F]' : 'bg-[#8A93A6]'}`}></div>
                <span className="text-sm text-white">Auto Branding</span>
              </div>
            </div>

            {autoPilotSettings.enabled && (
              <div className="p-4 rounded-xl bg-[#3A7BFF]/10 border border-[#3A7BFF]/20">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-white">{autoPilotSettings.total_runs}</p>
                    <p className="text-xs text-[#8A93A6]">Total Runs</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#39E6C8]">{autoPilotSettings.total_assets_processed}</p>
                    <p className="text-xs text-[#8A93A6]">Assets Processed</p>
                  </div>
                  <div>
                    <p className="text-sm text-white">{autoPilotSettings.last_run_at ? new Date(autoPilotSettings.last_run_at).toLocaleDateString() : 'Never'}</p>
                    <p className="text-xs text-[#8A93A6]">Last Run</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* EasyMoney Integration - REMOVED (deprecated) */}
    </div>
  );
};

export default Intelligence;
